import type { DayPart } from '../../../pages/Campaigns/Form/Content/Blocks/Delivery/types';
import type { LineItemForm } from '../../../pages/Campaigns/Form/types';
import { ANALYTICS_PROPERTY_KEYS } from '../constants';
import type { AnalyticsEventProperties } from '../types';
import { hasAdvancedDeliveryControls } from '../utils/hasAdvancedDeliveryControls';

export type DeliveryAdsSummaryContext = {
  campaignId?: string;
  lineItemId?: string;
  adAccountId?: string;
  revisionNumber?: string;
  isValidatedEdit?: boolean;
};

const getActiveDaypartCount = (dayParting: DayPart[]): number =>
  dayParting.filter((row) => row?.activeDayList?.some(Boolean)).length;

const toArrayProperty = (values: string[]): string[] | undefined => (values.length > 0 ? values : undefined);

const createConfigurationId = (context: DeliveryAdsSummaryContext): string =>
  `${context.lineItemId ?? 'line-item'}-${context.revisionNumber ?? 'draft'}`;

export const buildDeliveryAdsSummary = (
  formValues: LineItemForm,
  context: DeliveryAdsSummaryContext
): AnalyticsEventProperties => {
  const adNames = (formValues.ads ?? [])
    .map((ad) => ad?.creative?.asset?.name ?? ad?.creative?.id)
    .filter((value): value is string => Boolean(value));
  const adIds = (formValues.ads ?? []).map((ad) => ad?.creative?.id).filter((value): value is string => Boolean(value));
  const adWeights = (formValues.ads ?? [])
    .map((ad) => ad?.weight)
    .filter((weight): weight is number => typeof weight === 'number')
    .map(String);

  return {
    [ANALYTICS_PROPERTY_KEYS.CONFIGURATION_ID]: createConfigurationId(context),
    [ANALYTICS_PROPERTY_KEYS.CAMPAIGN_ID]: context.campaignId,
    [ANALYTICS_PROPERTY_KEYS.AD_ACCOUNT_ID]: context.adAccountId,
    [ANALYTICS_PROPERTY_KEYS.LINE_ITEM_ID]: context.lineItemId,
    [ANALYTICS_PROPERTY_KEYS.REVISION_NUMBER]: context.revisionNumber,
    [ANALYTICS_PROPERTY_KEYS.IS_VALIDATED_EDIT]: context.isValidatedEdit,
    [ANALYTICS_PROPERTY_KEYS.PACING_TYPE]: formValues.pacingType || undefined,
    [ANALYTICS_PROPERTY_KEYS.FREQUENCY]: formValues.frequency || undefined,
    [ANALYTICS_PROPERTY_KEYS.FREQUENCY_CAP_UNIT]: formValues.frequencyCapUnit || undefined,
    [ANALYTICS_PROPERTY_KEYS.HAS_ADVANCED_DELIVERY_CONTROLS]: hasAdvancedDeliveryControls(formValues),
    [ANALYTICS_PROPERTY_KEYS.DAYPART_SCHEDULE_COUNT]: getActiveDaypartCount(formValues.dayParting),
    [ANALYTICS_PROPERTY_KEYS.PACING_SCHEDULE_COUNT]: formValues.pacingSchedules?.length ?? 0,
    [ANALYTICS_PROPERTY_KEYS.AD_COUNT]: adNames.length,
    [ANALYTICS_PROPERTY_KEYS.AD_NAMES]: toArrayProperty(adNames),
    [ANALYTICS_PROPERTY_KEYS.AD_IDS]: toArrayProperty(adIds),
    [ANALYTICS_PROPERTY_KEYS.AD_WEIGHTS]: toArrayProperty(adWeights),
  };
};
